import { useEffect, useRef } from 'react';
import {
  useLazyGetPricingsQuery,
  usePatchPricingMutation,
} from 'app/services/projectBoardApi';
import type { IPricingCeleryData, IPricingRow } from 'models';
import { useTabAccess } from '../../hooks/useProjectBoardData';
import { useCeleryReport } from '../../hooks/useCeleryReport';
import { generationTimeStamp } from '../../projectBoardUtils';
import FormattedNumberInput from '../costing/FormattedNumberInput';
import { GridRow, MoneyCol, PlainCol, PctAmtCol } from '../costing/gridCells';
import FooterNotes, {
  MODINE_COIL_NOTE,
  ZERO_COST_MATERIAL_NOTE,
  footerNoteClasses,
} from '../costing/footerNotes';
import { track } from 'app/analytics';

/**
 * PRICING TAB (`#pricing` — `pricing/view/pricing.js` + pricing.tmpl),
 * inventory §B3 / parity 164–167. Same chassis as Costing with the documented
 * differences:
 *
 * - Boot order: preloader (bare `.col-xs-12.dotted-preloader.active` div) →
 *   celery `POST /reports/pricing_project` FIRST, then
 *   `GET /api/v1.0/projects/pricings/` (lazy query triggered when the first
 *   round trip lands), then render. RECALC re-runs ONLY the celery leg
 *   (parity 164 — it does NOT refetch the stored pricing row).
 * - Edits PATCH `/projects/pricings/{project_id}` with the PINNED body
 *   `{generate_id, price_sched, field}` (see patchPricing — price_sched only
 *   exists after a previous echo; an EMPTY value saves null and carries the
 *   row's previous generate_id, not the current celery one). `set_values`
 *   overrides display after the server recompute (parity 166).
 * - LIVE backspace handler (unlike costing's [NEG] twin): keyCode 8 on a
 *   `== 0` value just re-renders; otherwise it routes the pre-deletion value
 *   through the change path (FormattedNumberInput onBackspace).
 * - No realtime: PricingModel 'model.project.pricing' has NO models.mapping
 *   entry [NEG] — no socket listener here (documented parity 167).
 * - Outbound Freight data-boundparameter values use the REVERSED names
 *   (`freight_cost_add_amount_actual` / `freight_cost_add_percent_actual`)
 *   that match no input — harmless dead attribute, replicated [NEG-harmless].
 * - The Multiplier input always renders `pricingActual.mfg_price_multiplier`
 *   (the tmpl has no set_values branch for it — the computed override is dead
 *   data; the optimistic set in patchPricing keeps the typed value visible).
 */

const PricingTab = ({ projectId }: { projectId: number }) => {
  const access = useTabAccess(projectId, 'pricing');
  const [fetchPricings, { data: pricings }] = useLazyGetPricingsQuery();
  const [patchPricing] = usePatchPricingMutation();

  // Celery first (boot order parity) — ready immediately on mount.
  const celery = useCeleryReport<IPricingCeleryData>(
    '/reports/pricing_project',
    projectId,
    true,
    'pricing_data',
    (trigger) => track('pricing_run', { trigger })
  );

  // PricingCollection.fetch() chained after the FIRST celery result (RECALC
  // flips pending again but must not refetch — one-shot ref).
  const fetchedRef = useRef(false);
  useEffect(() => {
    if (!celery.pending && !fetchedRef.current) {
      fetchedRef.current = true;
      fetchPricings();
    }
  }, [celery.pending, fetchPricings]);

  const row: IPricingRow | undefined = pricings?.find(
    (r) => r.project_id === projectId
  );
  const accounting = celery.data;

  if (celery.pending || !accounting || !row) {
    // initialize/refresh preloader: bare div appended to #pricing.
    return <div className="col-xs-12 dotted-preloader active" />;
  }

  const locked = !access; // parity 167: everything disabled without access

  // Pricing has no standing note of its own, only these two conditional ones.
  // The zero-cost line goes above the Modine one, keeping it the note above the
  // last one here too (see costing/footerNotes.tsx).
  const notes = [
    ...(accounting.has_zero_cost_material ? [ZERO_COST_MATERIAL_NOTE] : []),
    ...(accounting.has_modine_coil ? [MODINE_COIL_NOTE] : []),
  ];

  /** set_values display precedence ({{#ifCompare … '!=' null}}). */
  const setValue = (field: string): unknown => {
    const v = row.set_values?.[field];
    return v !== null && v !== undefined ? v : undefined;
  };
  const actual = (field: keyof IPricingRow & string): number | string | null | undefined =>
    (setValue(field) as number | string | null | undefined) ??
    (row[field] as number | string | null | undefined);

  // channge_input: truthy value → set {field, generate_id(current celery)};
  // empty/0 → set {field: null} WITHOUT generate_id (the PATCH then carries
  // the row's previous generate_id/price_sched attrs — model onchange).
  const commit = (name: string, v: number | null) => {
    if (v) {
      patchPricing({
        projectId,
        set: { [name]: v, generate_id: celery.generateId ?? undefined },
        body: {
          generate_id: celery.generateId ?? undefined,
          price_sched: row.price_sched,
          [name]: v,
        },
      });
    } else {
      patchPricing({
        projectId,
        set: { [name]: null },
        body: {
          generate_id: row.generate_id ?? undefined,
          price_sched: row.price_sched,
          [name]: null,
        },
      });
    }
  };

  const onRecalc = () => {
    if (locked) return;
    celery.recalc();
  };

  return (
    <div className="content col-xs-12">
      <div className="time-stamp row">Generated on : {generationTimeStamp()}</div>
      {/* Pricing carries no footer note of its own, so `costing-content-item`
          (the bottom padding the absolutely-positioned note sits in) only goes
          on when the Modine note renders. See costing/footerNotes.tsx. */}
      <div className={`content-item row${footerNoteClasses(notes)}`}>
        <div className="col-lg-8 col-md-9 col-xs-12">
          <div className="row">
            <div className="input-label-group-heading list-inline col-xs-12">
              <div className="col-xs-3">
                <div className="row">
                  <h3 className="left">DESCRIPTION</h3>
                </div>
              </div>
              <div className="col-xs-9">
                <div className="col-xs-6">
                  <div className="row">
                    <h3>SCHEDULED</h3>
                  </div>
                </div>
                <div className="col-xs-6">
                  <div className="row">
                    <h3>ACTUAL</h3>
                  </div>
                </div>
              </div>
            </div>
            <div className="input-label-group sched-act-uom col-xs-12">
              <div className="row ">
                <GridRow label="Project List Price" className="costing-item top-margin">
                  <MoneyCol>
                    <FormattedNumberInput
                      value={accounting.price_list_sched}
                      mode="integer"
                      className="output-field number-field"
                      output
                      readOnlyAttr
                      tabIndex={-1}
                      disabled={locked}
                    />
                  </MoneyCol>
                  <MoneyCol>
                    <FormattedNumberInput
                      value={accounting.price_list_actual}
                      mode="integer"
                      className="price output-field number-field"
                      max="10000000"
                      output
                      readOnlyAttr
                      tabIndex={-1}
                      disabled={locked}
                    />
                  </MoneyCol>
                </GridRow>
                <GridRow label="Multiplier">
                  <PlainCol>
                    <FormattedNumberInput
                      value={accounting.mfg_multiplier_sched}
                      mode="float"
                      className="output-field number-field"
                      output
                      readOnlyAttr
                      tabIndex={-1}
                      disabled={locked}
                    />
                  </PlainCol>
                  <PlainCol>
                    <FormattedNumberInput
                      name="mfg_price_multiplier"
                      value={row.mfg_price_multiplier}
                      mode="float"
                      className="price number-field change_field"
                      max="10000000"
                      disabled={locked}
                      onCommit={commit}
                      onBackspace={commit}
                    />
                  </PlainCol>
                </GridRow>
                <GridRow label="Project Net Price">
                  <MoneyCol>
                    <FormattedNumberInput
                      value={accounting.price_sched}
                      mode="integer"
                      className="price_sched output-field number-field"
                      output
                      readOnlyAttr
                      tabIndex={-1}
                      disabled={locked}
                    />
                  </MoneyCol>
                  <MoneyCol>
                    {row.price_actual ? (
                      <FormattedNumberInput
                        value={row.price_actual}
                        mode="integer"
                        className="output-field number-field"
                        output
                        readOnlyAttr
                        tabIndex={-1}
                        disabled={locked}
                      />
                    ) : (
                      <FormattedNumberInput
                        value={accounting.price_actual}
                        mode="integer"
                        className="price output-field number-field"
                        output
                        readOnlyAttr
                        tabIndex={-1}
                        disabled={locked}
                      />
                    )}
                  </MoneyCol>
                </GridRow>
                <GridRow label="Discount">
                  <PctAmtCol
                    offset
                    pct={
                      <FormattedNumberInput
                        name="price_discount_actual_percent"
                        value={actual('price_discount_actual_percent')}
                        mode="decimal"
                        className="percent change_field number-field"
                        min="0.0"
                        step="0.1"
                        max="100.00"
                        boundParameter="price_discount_amount_actual"
                        disabled={locked}
                        onCommit={commit}
                        onBackspace={commit}
                      />
                    }
                    amt={
                      <FormattedNumberInput
                        name="price_discount_amount_actual"
                        value={actual('price_discount_amount_actual')}
                        mode="integer"
                        className="price change_field number-field"
                        min="0"
                        step="1"
                        max="10000000"
                        boundParameter="price_discount_actual_percent"
                        disabled={locked}
                        onCommit={commit}
                        onBackspace={commit}
                      />
                    }
                  />
                </GridRow>
                <GridRow label="Outbound Freight">
                  <PctAmtCol
                    pct={
                      <FormattedNumberInput
                        value={accounting.freight_cost_add_sched_percent}
                        mode="decimal"
                        className="output-field number-field"
                        output
                        readOnlyAttr
                        tabIndex={-1}
                        disabled={locked}
                      />
                    }
                    amt={
                      <FormattedNumberInput
                        value={accounting.freight_cost_add_sched_amount}
                        mode="integer"
                        className="output-field number-field"
                        output
                        readOnlyAttr
                        tabIndex={-1}
                        disabled={locked}
                      />
                    }
                  />
                  <PctAmtCol
                    pct={
                      <FormattedNumberInput
                        name="freight_cost_add_actual_percent"
                        value={actual('freight_cost_add_actual_percent')}
                        mode="decimal"
                        className="percent change_field number-field"
                        max="100"
                        boundParameter="freight_cost_add_amount_actual"
                        disabled={locked}
                        onCommit={commit}
                        onBackspace={commit}
                      />
                    }
                    amt={
                      <FormattedNumberInput
                        name="freight_cost_add_actual_amount"
                        value={actual('freight_cost_add_actual_amount')}
                        mode="integer"
                        className="price change_field number-field"
                        max="10000000"
                        boundParameter="freight_cost_add_percent_actual"
                        disabled={locked}
                        onCommit={commit}
                        onBackspace={commit}
                      />
                    }
                  />
                </GridRow>
                <GridRow label="Project Total Net Price" className="costing-item bold">
                  <MoneyCol>
                    <FormattedNumberInput
                      value={accounting.price_net_sched}
                      mode="integer"
                      className="output-field number-field"
                      output
                      readOnlyAttr
                      tabIndex={-1}
                      disabled={locked}
                    />
                  </MoneyCol>
                  <MoneyCol>
                    <FormattedNumberInput
                      value={
                        row.price_net_actual
                          ? row.price_net_actual
                          : accounting.price_net_actual
                      }
                      mode="integer"
                      className="output-field number-field"
                      output
                      readOnlyAttr
                      tabIndex={-1}
                      disabled={locked}
                    />
                  </MoneyCol>
                </GridRow>
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-4 col-md-3 col-xs-12 reload_button">
          <button
            id="refresh_pricing"
            className={`btn-add right${locked ? ' non-clickable' : ''}`}
            onClick={onRecalc}
          >
            RECALC
          </button>
        </div>
        <FooterNotes notes={notes} />
      </div>
    </div>
  );
};

export default PricingTab;
